import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { API_BASE_URL } from '../../utils/constants';
import { FaClock, FaPlus, FaTrash, FaSave, FaHome, FaVideo, FaPhone, FaComments, FaStethoscope } from 'react-icons/fa';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './ManageOpeningHoursScreen.css';

const ManageOpeningHoursScreen = () => {
  const { user, setUser } = useUser();
  const [availability, setAvailability] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedConsultationTypes, setSelectedConsultationTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  const timeOptions = Array.from({ length: 24 }, (_, i) => {
    const hour = i % 12 === 0 ? 12 : i % 12;
    const period = i < 12 ? 'AM' : 'PM';
    return `${hour < 10 ? '0' : ''}${hour}:00 ${period}`;
  });

  const consultationTypes = [
    { key: 'inClinic', label: 'In-Clinic', icon: <FaStethoscope /> },
    { key: 'homeVisit', label: 'Home Visit', icon: <FaHome /> },
    { key: 'videoConsultation', label: 'Video Call', icon: <FaVideo /> },
    { key: 'teleConsultation', label: 'Phone Call', icon: <FaPhone /> },
    { key: 'chatConsultation', label: 'Chat', icon: <FaComments /> },
  ];

  useEffect(() => {
    initializeAvailability();
  }, [user]);

  const initializeAvailability = () => {
    const initialAvailability = daysOfWeek.map((day) => {
      const dayData = user?.availableSlots?.find((slot) => slot.dayOfWeek === day);
      return {
        day,
        slots: dayData?.slots || [],
        status: !!dayData?.slots?.length,
      };
    });
    setAvailability(initialAvailability);
  };

  const openModal = (day) => {
    setSelectedDay(day);
    setStartTime('');
    setEndTime('');
    setSelectedConsultationTypes([]);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedDay('');
    setStartTime('');
    setEndTime('');
    setSelectedConsultationTypes([]);
  };

  const toggleConsultationType = (key) => {
    setSelectedConsultationTypes((prev) =>
      prev.includes(key) ? prev.filter((type) => type !== key) : [...prev, key]
    );
  };

  const addTimeSlot = () => {
    if (!startTime || !endTime) {
      toast.error('Please specify both start and end times.');
      return;
    }

    if (timeOptions.indexOf(startTime) >= timeOptions.indexOf(endTime)) {
      toast.error('End time must be after start time.');
      return;
    }

    if (selectedConsultationTypes.length === 0) {
      toast.error('Please select at least one consultation type.');
      return;
    }

    setAvailability((prev) =>
      prev.map((item) =>
        item.day === selectedDay
          ? {
              ...item,
              slots: [...item.slots, { startTime, endTime, slotFor: selectedConsultationTypes }],
              status: true,
            }
          : item
      )
    );

    closeModal();
  };

  const removeTimeSlot = (day, index) => {
    setAvailability((prev) =>
      prev.map((item) =>
        item.day === day
          ? {
              ...item,
              slots: item.slots.filter((_, i) => i !== index),
              status: item.slots.filter((_, i) => i !== index).length > 0,
            }
          : item
      )
    );
  };

  const toggleDay = (day) => {
    setAvailability((prev) =>
      prev.map((item) =>
        item.day === day ? { ...item, status: !item.status } : item
      )
    );
  };

  const saveAvailability = async () => {
    const availableSlots = availability
      .filter((item) => item.status && item.slots.length > 0)
      .map((item) => ({
        dayOfWeek: item.day,
        status: item.status,
        slots: item.slots.map((slot) => ({
          startTime: slot.startTime,
          endTime: slot.endTime,
          slotFor: slot.slotFor,
        })),
      }));

    try {
      setLoading(true);
      const accessToken = localStorage.getItem('ACCESS_TOKEN');

      if (!accessToken) {
        throw new Error('Access token not found. Please log in again.');
      }

      const response = await fetch(`${API_BASE_URL}api/doctor/profile/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ availableSlots }),
      });

      if (response.ok) {
        setUser((prevUser) => ({
          ...prevUser,
          availableSlots,
        }));
        toast.success('Availability saved successfully!');
      } else {
        throw new Error('Failed to save availability');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save availability. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getConsultationIcon = (type) => {
    const consultation = consultationTypes.find((t) => t.key === type);
    return consultation ? consultation.icon : <FaStethoscope />;
  };

  return (
    <DashboardLayout title="Manage Opening Hours" showBackButton={true}>
      <div className="opening-hours-container">
        <div className="opening-hours-header">
          <h1>Manage Availability Hours</h1>
          <p>Manage your availability by adding or removing time slots for each day.</p>
        </div>

        <div className="availability-list">
          {availability.map((item) => (
            <div key={item.day} className="availability-card">
              <div className="day-header">
                <div className="day-info">
                  <h3 className="day-name">{item.day}</h3>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={item.status}
                      onChange={() => toggleDay(item.day)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>

              {item.status && (
                <div className="slots-section">
                  {item.slots.length > 0 ? (
                    <div className="slots-list">
                      {item.slots.map((slot, index) => (
                        <div key={index} className="slot-item">
                          <div className="slot-info">
                            <div className="slot-time">
                              <FaClock className="time-icon" />
                              {slot.startTime} - {slot.endTime}
                            </div>
                            <div className="slot-types">
                              {slot.slotFor?.map((type) => (
                                <span key={type} className="consultation-type">
                                  {getConsultationIcon(type)}
                                </span>
                              ))}
                            </div>
                          </div>
                          <button
                            className="remove-slot-btn"
                            onClick={() => removeTimeSlot(item.day, index)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-slots">
                      <p>No slots added</p>
                    </div>
                  )}

                  <button
                    className="add-slot-btn"
                    onClick={() => openModal(item.day)}
                  >
                    <FaPlus />
                    Add Slot
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <button
          className="save-availability-btn"
          onClick={saveAvailability}
          disabled={loading}
        >
          <FaSave />
          {loading ? 'Saving...' : 'Save Availability'}
        </button>

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Add Time Slot for {selectedDay}</h3>
                <button className="close-modal-btn" onClick={closeModal}>
                  ×
                </button>
              </div>

              <div className="modal-body">
                <div className="time-selection">
                  <div className="time-group">
                    <label>Start Time</label>
                    <select
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="time-select"
                    >
                      <option value="">Select Start Time</option>
                      {timeOptions.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="time-group">
                    <label>End Time</label>
                    <select
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="time-select"
                    >
                      <option value="">Select End Time</option>
                      {timeOptions.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="consultation-types-section">
                  <label>Consultation Types</label>
                  <div className="consultation-types-grid">
                    {consultationTypes.map((type) => (
                      <div
                        key={type.key}
                        className={`consultation-type-item ${
                          selectedConsultationTypes.includes(type.key) ? 'selected' : ''
                        }`}
                        onClick={() => toggleConsultationType(type.key)}
                      >
                        <div className="type-icon">{type.icon}</div>
                        <span className="type-label">{type.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button className="add-time-slot-btn" onClick={addTimeSlot}>
                  Add Time Slot
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ManageOpeningHoursScreen;